import { useOnRouteExit } from "@/composables/use-on-route-exit";
import {
  GetShowDocument,
  GetShowStaticDocument,
  ShowSimulcastInstanceStatus,
  ShowStatus,
  type ThemeFragment,
} from "@/generated/graphql";
import { isSimulfestSession } from "@/utils/simulfest";
import { defineStore } from "pinia";
import { computed, inject, onScopeDispose, ref, watch } from "vue";
import { useDebounceFn } from "@vueuse/core";
import { useRoute } from "vue-router";
import { useChannelStore } from "./channel.store";
import { useAppStore } from "./app.store";
import { ablySdkInjectionKey } from "@/composables/ably/use-ably-sdk";
import { RouteNames } from "@/router/app.router";
import type { DocumentType } from "@/generated";
import { graphQlClient } from "@/utils/urql";
import type { ISimulfestHostAlert } from "@/features/show/services/sockets/sockets-service.types";

export type StreamHealthStatus =
  | "healthy"
  | "issue_detected"
  | "recovering"
  | "recovered"
  | "failed";

function useShowFromRoute() {
  const route = useRoute();

  const showId = computed(() => (route.params.show as string) ?? "");
  const show = ref<NonNullable<
    DocumentType<typeof GetShowDocument>["show"]
  > | null>(null);
  const showStatus = ref<ShowStatus | null>(null);

  async function refetch(cacheBustToken?: string) {
    if (!showId.value) return;

    /// Doing a manual fetch here because we need to refetch the show
    /// when the route changes and that didn't seem to work with
    /// the useQuery hook even when using a ref for the variables
    /// and doing manual refetching and loading and restarting.
    /// If you spend time on this and can't get it to work, just
    /// increase this: timeSpentOnThis = 2hrs.
    // Build a custom URL with _t query param to bust CDN edge cache.
    // Pass a cacheBustToken (e.g. Date.now().toString()) to bypass CDN cache for mutation-driven refreshes.
    let hotUrl: string | undefined;
    if (cacheBustToken) {
      const base = new URL(
        import.meta.env.VITE_GRAPHQL_URL,
        window.location.origin,
      );
      base.searchParams.set("_t", cacheBustToken);
      hotUrl = base.toString();
    }

    // Fire GetShow (hot fields) and GetShowStatic (static slice, long-TTL CDN) in parallel.
    // GetShow carries hot-path fields (status, simulcast, mux, agenda); GetShowStatic carries
    // static metadata (title, theme, channel, authorization rules, etc.) at a 600s CDN TTL.
    const [hotResult, staticResult] = await Promise.all([
      graphQlClient.value.query(
        GetShowDocument,
        { id: showId.value },
        {
          requestPolicy: "network-only",
          ...(hotUrl && { url: hotUrl }),
        },
      ),
      graphQlClient.value.query(
        GetShowStaticDocument,
        { show_id: showId.value },
        { requestPolicy: "cache-first" },
      ),
    ]);

    // Merge static fields into the hot result so the show ref has the full shape.
    // Static fields (title, channel, theme, auth rules, etc.) come from GetShowStatic (long-TTL CDN).
    // Hot fields (status, simulcast, mux, agenda) come from GetShow (uncached, DDB hot-state).
    const hotShow = hotResult.data?.show ?? null;
    const staticShow = staticResult.data?.getShowStatic ?? null;
    const merged = hotShow
      ? ({ ...staticShow, ...hotShow } as NonNullable<typeof hotShow>)
      : staticShow
        ? ({ ...staticShow } as NonNullable<typeof hotShow>)
        : null;
    show.value = merged;
    showStatus.value = show.value?.status ?? null;
  }

  watch(
    showId,
    async (newId, oldId) => {
      // When navigating between shows in-app, clear stale data first so
      // consumers don't render the previous show's state during the gap
      // before the new show's queries resolve. (Initial mount: oldId is
      // undefined, so this short-circuits and we go straight to refetch.)
      if (oldId && newId !== oldId) {
        show.value = null;
        showStatus.value = null;
      }
      await refetch();
    },
    { immediate: true },
  );

  return { showId, show, showStatus, refetch };
}

export const useShowStore = defineStore("show", () => {
  /// Show state
  const viewers = ref(0);
  const isSimulfest = computed(() => isSimulfestSession());
  const pulsateStep = ref<"RTMP_BOX" | "PREPARE_SHOW" | null>(null);

  const { showId, show, showStatus, refetch } = useShowFromRoute();
  const chatRoomId = computed(
    () => show.value?.show_chat_room?.chat_room_id ?? null,
  );
  const hasShowData = computed(() => show.value !== null);
  const channelStore = useChannelStore();

  // Global state
  const isLive = computed(() => showStatus.value === ShowStatus.Live);
  const isEnded = computed(() => showStatus.value === ShowStatus.Ended);
  const isVideoCast = computed(() => showStatus.value === ShowStatus.VideoCast);
  const isHost = computed(() => channelStore.permissions.produceShows);
  const usesRtmp = computed(() => show.value?.uses_rtmp ?? false);

  // Stream health state (populated by host alert events from T-030/T-031)
  const streamHealthStatus = ref<StreamHealthStatus>("healthy");
  const streamHealthVariant = ref<string | null>(null);
  const lastAlertTimestamp = ref<string | null>(null);

  const hasStreamIssue = computed(
    () =>
      streamHealthStatus.value !== "healthy" &&
      streamHealthStatus.value !== "recovered",
  );

  const hasFailedSimulfestInstances = computed(
    () =>
      show.value?.show_simulcast_instances?.some(
        // "failed" not yet in generated enum but may appear at runtime (T-014)
        (instance) =>
          instance.status === ("failed" as ShowSimulcastInstanceStatus),
      ) ?? false,
  );

  function updateStreamHealth(alert: ISimulfestHostAlert) {
    // Out-of-order event protection: ignore events older than the last processed alert
    if (
      alert.timestamp &&
      lastAlertTimestamp.value &&
      alert.timestamp < lastAlertTimestamp.value
    ) {
      return;
    }
    lastAlertTimestamp.value = alert.timestamp ?? new Date().toISOString();
    streamHealthVariant.value = alert.variant;
    switch (alert.alertType) {
      case "instance_failed":
        streamHealthStatus.value = "issue_detected";
        break;
      case "instance_recovering":
        streamHealthStatus.value = "recovering";
        break;
      case "instance_recovered":
        streamHealthStatus.value = "recovered";
        break;
      case "recovery_exhausted":
        streamHealthStatus.value = "failed";
        break;
    }
  }

  function resetStreamHealth() {
    streamHealthStatus.value = "healthy";
    streamHealthVariant.value = null;
    lastAlertTimestamp.value = null;
  }

  // Fan state
  /**
   * Used to indicate if user is still watching after a show after host has ended it.
   * Need this in order not to lose last couple of seconds when host ends the show.
   */
  const isUserStillWatching = ref(false);
  let stillWatchingTimerId: ReturnType<typeof setTimeout> | null = null;

  function startStillWatchingTimer(durationMs = 15_000) {
    // Only start if the show just ended — don't restart on duplicate events
    if (isUserStillWatching.value) return;

    isUserStillWatching.value = true;

    stillWatchingTimerId = setTimeout(() => {
      isUserStillWatching.value = false;
      stillWatchingTimerId = null;
    }, durationMs);
  }

  function stopStillWatchingTimer() {
    isUserStillWatching.value = false;
    if (stillWatchingTimerId) {
      clearTimeout(stillWatchingTimerId);
      stillWatchingTimerId = null;
    }
  }

  /**
   * Use this property to tell if there are no simulcast instances
   * pending or running.
   *
   * This is useful to support reboot of the show in case of catastrophic
   * failure of simulfest instances since we don't rely just on checking
   * for no simulfest instances (or length 0) to show start simulfest
   * button or handle fallback logic.
   */
  const hasNoActionableSimulfestInstances = computed(() => {
    const instances = show.value?.show_simulcast_instances;
    if (!instances) return true;

    // "failed" is not yet in the generated enum (codegen pending T-014 schema update),
    // but the API may return it at runtime. Treat it as non-actionable like "ended".
    const isNonActionable = (status: string) =>
      status === ShowSimulcastInstanceStatus.Ended || status === "failed";

    const hasActionableInstances = instances.some(
      (instance) => !isNonActionable(instance.status),
    );
    return !hasActionableInstances;
  });

  watch(
    [isUserStillWatching, hasNoActionableSimulfestInstances],
    ([stillWatching, noActionableInstances]) => {
      if (stillWatching && noActionableInstances) {
        isUserStillWatching.value = false;
      }
    },
  );

  const isPreparingShow = computed(() => {
    const instances = show.value?.show_simulcast_instances;
    if (!instances) return false;

    // If there are no simulcast instances, then simulfest has not started
    if (hasNoActionableSimulfestInstances.value) return false;

    return instances.some(
      (instance) => instance.status === ShowSimulcastInstanceStatus.Pending,
    );
  });
  const isSimulfestLive = computed(() => {
    // If there are no simulcast instances, then simulfest has not started
    if (hasNoActionableSimulfestInstances.value) return false;

    // "failed" is not yet in the generated enum but may appear at runtime (T-014)
    const isTerminal = (status: string) =>
      status === ShowSimulcastInstanceStatus.Ended || status === "failed";

    return show?.value?.show_simulcast_instances?.every(
      (instance) =>
        instance.status === ShowSimulcastInstanceStatus.Live ||
        isTerminal(instance.status),
    );
  });

  const theme = computed(() => {
    return show.value?.show_themes?.[0]?.theme_configuration as ThemeFragment;
  });

  const participantsCount = computed(() => show.value?.participants_count ?? 0);

  function resetSessionState() {
    viewers.value = 0;
    resetStreamHealth();
    stopStillWatchingTimer();
  }
  useOnRouteExit(RouteNames.Show, resetSessionState);

  const debouncedSimulfestRefetch = useDebounceFn(() => refetch(), 300);
  onScopeDispose(() =>
    (
      debouncedSimulfestRefetch as unknown as { cancel?: () => void }
    ).cancel?.(),
  );

  // Backstop poll: when a simulcast instance flips to booted_up the API
  // creates the Mux livestream sequentially AFTER the Ably delta is pushed
  // (Mux API roundtrip ~1-3s). The 300ms debounced refetch above typically
  // fires BEFORE mux_livestreams is populated server-side, after which no
  // further refetch is triggered — leaving the host stuck on "Generating
  // stream credentials" and starving the Mux player polling. Re-poll for
  // up to ~10s to catch the late-arriving mux row.
  let muxBackstopTimer: ReturnType<typeof setTimeout> | null = null;
  const cancelMuxBackstop = () => {
    if (muxBackstopTimer) {
      clearTimeout(muxBackstopTimer);
      muxBackstopTimer = null;
    }
  };
  onScopeDispose(cancelMuxBackstop);
  function scheduleMuxBackstopPoll() {
    if (show.value?.mux_livestreams?.length) return;
    cancelMuxBackstop();
    let attempts = 0;
    const MAX_ATTEMPTS = 7; // 7 attempts × 1.5s ≈ 10s window
    const tick = async () => {
      muxBackstopTimer = null;
      if (show.value?.mux_livestreams?.length) return;
      if (++attempts > MAX_ATTEMPTS) return;
      await refetch();
      if (show.value?.mux_livestreams?.length) return;
      muxBackstopTimer = setTimeout(tick, 1500);
    };
    muxBackstopTimer = setTimeout(tick, 800);
  }

  // Heal drift from lost/missing Ably deltas: any single dropped delta on
  // `simulcast-state` or `show-state` leaves the FE permanently stale
  // until the next delta arrival. We add three orthogonal healing
  // triggers so the FE can self-correct without a manual refresh.
  const appStore = useAppStore();
  // Use raw inject (instead of useAblySdk) so the store stays instantiable
  // in tests/contexts where the SDK is not provided. The reconnect-driven
  // refetch trigger silently no-ops in that case; the focus + failsafe
  // poll triggers remain active.
  const ablySdk = inject(ablySdkInjectionKey, null);
  const debouncedResiliencyRefetch = useDebounceFn(() => refetch(), 250);
  onScopeDispose(() =>
    (
      debouncedResiliencyRefetch as unknown as { cancel?: () => void }
    ).cancel?.(),
  );

  watch(
    () => appStore.appHasFocus,
    (hasFocus, hadFocus) => {
      if (hasFocus && !hadFocus && showId.value) {
        debouncedResiliencyRefetch();
      }
    },
  );

  if (ablySdk) {
    watch(
      () => ablySdk.isConnected.value,
      (connected, wasConnected) => {
        if (connected && !wasConnected && showId.value) {
          debouncedResiliencyRefetch();
        }
      },
    );
  }

  // Failsafe poll for clients that *consume* the orchestration state —
  // hosts (driving the show) and simulfest containers (rendering it for
  // recording). Catches the "delta was never published" mode that no
  // arrival-driven trigger can heal. Skipped for fans (their surfaces
  // are healed by tab-focus + reconnect alone) and for concluded shows.
  const FAILSAFE_POLL_INTERVAL_MS = 30_000;
  const shouldRunFailsafePoll = computed(() => {
    if (!showId.value) return false;
    if (!isHost.value && !isSimulfest.value) return false;
    if (!appStore.appHasFocus) return false;
    const status = show.value?.status;
    return status !== ShowStatus.Ended && status !== ShowStatus.VideoCast;
  });

  let failsafePollTimer: ReturnType<typeof setInterval> | null = null;
  const stopFailsafePoll = () => {
    if (failsafePollTimer) {
      clearInterval(failsafePollTimer);
      failsafePollTimer = null;
    }
  };
  watch(
    shouldRunFailsafePoll,
    (active) => {
      stopFailsafePoll();
      if (!active) return;
      failsafePollTimer = setInterval(() => {
        refetch(Date.now().toString());
      }, FAILSAFE_POLL_INTERVAL_MS);
    },
    { immediate: true },
  );
  onScopeDispose(stopFailsafePoll);

  function updateSimulfestInstance(
    instanceId: string,
    status: ShowSimulcastInstanceStatus,
  ) {
    if (show.value === null) return;

    // Since we get simulfest updates every time we send a update to simulfest
    // we need to make sure that we're not doing any duplicate updates
    const simulfestInstance = show.value.show_simulcast_instances?.find(
      (instance) => instance.id === instanceId,
    );

    if (simulfestInstance === undefined) {
      console.error(
        `Could not find simulfest instance ${instanceId}. Refetching show state.`,
      );
      refetch();
      return;
    }

    if (simulfestInstance.status === status) {
      return;
    }

    console.log("Updating simulfest instance", instanceId, status);
    show.value = {
      ...show.value,
      show_simulcast_instances: show.value.show_simulcast_instances?.map(
        (instance) =>
          instance.id === instanceId ? { ...instance, status } : instance,
      ),
    };
    debouncedSimulfestRefetch(); // Debounced — prevents triple-refetch while still confirming from server

    if (status === ShowSimulcastInstanceStatus.BootedUp) {
      scheduleMuxBackstopPoll();
    }
  }

  /**
   * Wait until every simulcast instance has settled (`ended` / `failed`) or
   * until `maxMs` elapses, whichever comes first. Used by host end-show flows
   * so the confirmation modal stays in the loading state for the full
   * shutdown window — the simulfest container orchestrator has a ~10s grace
   * period to flush final HLS segments before reporting `ended`, during which
   * the host should not be able to dismiss the modal or mistake the show
   * for already-stopped.
   *
   * Resolves whether or not the timeout fires; callers don't differentiate
   * since either way they want to release the modal afterwards.
   */
  async function waitForSimulfestEnded(maxMs = 12_000): Promise<void> {
    const start = Date.now();
    // Already terminated (e.g. user re-clicked End on an already-ending show)
    if (hasNoActionableSimulfestInstances.value) return;
    while (Date.now() - start < maxMs) {
      if (hasNoActionableSimulfestInstances.value) return;
      await new Promise((r) => setTimeout(r, 500));
    }
  }

  return {
    chatRoomId,
    hasShowData,
    showId,
    show,
    showStatus,

    theme,
    participantsCount,

    viewers,
    isSimulfest,
    isSimulfestLive,
    pulsateStep,

    hasNoActionableSimulfestInstances,
    hasFailedSimulfestInstances,
    isPreparingShow,
    isLive,
    isEnded,
    isVideoCast,
    isHost,
    usesRtmp,
    isUserStillWatching,
    startStillWatchingTimer,
    stopStillWatchingTimer,

    // Stream health (T-031)
    streamHealthStatus,
    streamHealthVariant,
    hasStreamIssue,
    updateStreamHealth,
    resetStreamHealth,

    // Methods
    refetch,
    updateSimulfestInstance,
    waitForSimulfestEnded,
    $reset: resetSessionState,
  };
});
